import { useEffect, useRef, useState } from "react";
import { useV2PreviewChangeOrderMutation } from "@generated";
import { lineKey } from "./scopeLines";
import type {
  PreviewGovernance,
  PreviewLine,
  PreviewResult,
  Totals,
} from "./types";
import type { ChangeOrderBody } from "./payload";

export const PREVIEW_DEBOUNCE_MS = 400;

const totals = (raw: any): Totals => ({
  hours: Number(raw?.hours) || 0,
  revenue: Number(raw?.revenue) || 0,
  cost: Number(raw?.cost) || 0,
});

export const toPreviewResult = (meta: any): PreviewResult => ({
  lines: Object.fromEntries(
    ((meta?.lines ?? []) as PreviewLine[]).map((line) => [
      lineKey(line.type, String(line["source-id"])),
      { ...line, hours: Number(line.hours) || 0 },
    ])
  ),
  governances: ((meta?.governances ?? []) as PreviewGovernance[]).map((g) => ({
    ...g,
    hours: Number(g.hours) || 0,
  })),
  totals: totals(meta?.totals),
  parent: totals(meta?.parent),
});

// First JSON:API error detail on a rejected RTK Query call, if any.
const firstErrorDetail = (err: any): string | undefined => {
  const detail = err?.data?.errors?.[0]?.detail;
  return typeof detail === "string" && detail.trim() !== "" ? detail : undefined;
};

// Prices the draft on the server after the user pauses. Each request takes a
// sequence number; a response older than the latest request is dropped.
export const useChangeOrderPreview = (body: ChangeOrderBody | null) => {
  const [trigger] = useV2PreviewChangeOrderMutation();
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState(false);
  const [errorDetail, setErrorDetail] = useState<string | undefined>(undefined);
  const sequence = useRef(0);
  const key = body ? JSON.stringify(body) : null;

  useEffect(() => {
    const current = ++sequence.current;
    if (!body) {
      setIsCalculating(false);
      return;
    }
    setIsCalculating(true);
    const timer = setTimeout(async () => {
      try {
        const res: any = await trigger({ body } as any).unwrap();
        if (current !== sequence.current) return;
        setPreview(toPreviewResult(res?.meta));
        setError(false);
        setErrorDetail(undefined);
      } catch (err) {
        if (current !== sequence.current) return;
        console.error("Failed to preview change request", err);
        setError(true);
        setErrorDetail(firstErrorDetail(err));
      } finally {
        if (current === sequence.current) setIsCalculating(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // `key` stands in for `body`, whose identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { preview: body ? preview : null, isCalculating, error, errorDetail };
};

export default useChangeOrderPreview;
